import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { useSettings } from "@/context/SettingsContext";
import { useImg } from "@/lib/useImg";
import { imgSrc } from "@/lib/img";
import { IMG } from "@/data/images";

const SLIDES = 3;

const Slide1 = ({ image }) => (
  <PageHero
    fill
    eyebrow="Pure Nutrition. Real Superfoods."
    lines={["Nature's", "Finest Nutrition."]}
    sub="Pure nutrition, thoughtfully crafted to earn your trust — every single day."
    image={image}
    imagePosition="center 30%"
  >
    <div className="flex flex-wrap gap-4">
      <Link to="/collection" data-testid="hero-explore-collection-button" className="group inline-flex items-center gap-2.5 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold transition-colors duration-300">
        Explore All Products <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </Link>
      <Link to="/our-story" data-testid="hero-our-story-button" className="inline-flex items-center gap-2.5 border border-cream/60 text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-cream hover:text-forest transition-colors duration-300">
        Our Story
      </Link>
    </div>
  </PageHero>
);

const ImageContent = ({ src, hotspots = [], cta }) => (
  <>
    <img src={src} alt="" className="w-full h-full object-cover object-center bg-forestdeep" draggable="false" />
    <div className="hidden sm:block">
      {hotspots.map((h) => (
        <Link key={h.testid} to={h.to} data-testid={h.testid} aria-label={h.label} className="absolute z-20 rounded-full" style={{ left: h.left, top: h.top, width: h.width, height: h.height }} />
      ))}
    </div>
    {cta && (
      <Link
        to={cta.to}
        data-testid={cta.testid}
        className="group absolute z-20 inline-flex items-center gap-1.5 sm:gap-2 bg-forest text-cream text-[8px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full hover:bg-gold transition-colors duration-300 shadow-md"
        style={{ left: cta.left, bottom: cta.bottom }}
      >
        {cta.label} <ArrowRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 group-hover:translate-x-1 transition-transform" />
      </Link>
    )}
  </>
);

const slideVariants = {
  enter: (d) => ({ x: d >= 0 ? "100%" : "-100%" }),
  center: { x: 0 },
  exit: (d) => ({ x: d >= 0 ? "-100%" : "100%" }),
};

export const HeroCarousel = () => {
  const s = useSettings();
  const img = useImg();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [auto, setAuto] = useState(true);
  const sectionRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [ratios, setRatios] = useState({});
  const slide1Img = imgSrc(s.hero_image) || IMG.heroHome;
  const slide2Img = img("carouselSlide2");
  const slide3Img = img("carouselSlide3");
  const go = (n, d) => {
    setAuto(false);
    setDir(d);
    setI((n + SLIDES) % SLIDES);
  };

  // Auto-play once through all slides on load (5s each), then stop.
  useEffect(() => {
    if (!auto) return;
    if (i >= SLIDES - 1) {
      setAuto(false);
      return;
    }
    const t = setTimeout(() => {
      setDir(1);
      setI((p) => p + 1);
    }, 5000);
    return () => clearTimeout(t);
  }, [i, auto]);

  // Measure each slide image's natural aspect ratio (adapts to admin image swaps).
  useEffect(() => {
    [slide1Img, slide2Img, slide3Img].forEach((src, idx) => {
      if (!src) return;
      const im = new Image();
      im.onload = () => im.naturalHeight && setRatios((r) => ({ ...r, [idx]: im.naturalWidth / im.naturalHeight }));
      im.src = src;
    });
  }, [slide1Img, slide2Img, slide3Img]);

  // Size the carousel to the active slide so the full image shows (no crop, no bands).
  // Slide 1 carries hero copy, so keep a readable minimum height on small screens.
  useEffect(() => {
    const measure = () => {
      const w = sectionRef.current?.offsetWidth || window.innerWidth;
      const ratio = ratios[i];
      if (!ratio) {
        setHeight(Math.round(window.innerHeight * 0.82));
        return;
      }
      let h = Math.round(w / ratio);
      if (i === 0 && window.innerWidth < 640) h = Math.max(h, Math.round(window.innerHeight * 0.72));
      setHeight(h);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [i, ratios]);

  const renderSlide = (idx) => {
    if (idx === 0) return <Slide1 image={slide1Img} />;
    if (idx === 1)
      return (
        <ImageContent
          src={slide2Img}
          hotspots={[
            { testid: "slide2-founders-link", label: "Meet Our Founders", to: "/our-story", left: "3.5%", top: "60%", width: "21%", height: "12%" },
            { testid: "slide2-shop-link", label: "Shop Now", to: "/collection", left: "24.5%", top: "60%", width: "15%", height: "12%" },
          ]}
        />
      );
    return <ImageContent src={slide3Img} cta={{ testid: "slide3-shop-now", label: "Shop Now", to: "/collection", left: "5%", bottom: "5%" }} />;
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-carousel"
      className="relative overflow-hidden bg-forestdeep transition-[height] duration-700 ease-in-out"
      style={{ height: height ? `${height}px` : "82vh" }}
    >
      <AnimatePresence initial={false} custom={dir}>
        <motion.div
          key={i}
          data-testid={`hero-slide-${i + 1}`}
          className="absolute inset-0 z-10"
          custom={dir}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ x: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } }}
        >
          {renderSlide(i)}
        </motion.div>
      </AnimatePresence>

      <button onClick={() => go(i - 1, -1)} data-testid="hero-prev" aria-label="Previous slide" className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 text-cream/90 hover:text-cream transition-colors [filter:drop-shadow(0_1px_4px_rgba(0,0,0,0.55))]">
        <ChevronLeft size={34} strokeWidth={1.75} />
      </button>
      <button onClick={() => go(i + 1, 1)} data-testid="hero-next" aria-label="Next slide" className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 text-cream/90 hover:text-cream transition-colors [filter:drop-shadow(0_1px_4px_rgba(0,0,0,0.55))]">
        <ChevronRight size={34} strokeWidth={1.75} />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
        {[0, 1, 2].map((n) => (
          <button
            key={n}
            onClick={() => go(n, n > i ? 1 : -1)}
            data-testid={`hero-dot-${n + 1}`}
            aria-label={`Go to slide ${n + 1}`}
            className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${i === n ? "bg-gold" : "bg-cream/60 hover:bg-cream"}`}
          />
        ))}
      </div>
    </section>
  );
};
